// @flow
import Permissions from 'enums/permissions'
import * as React from 'react'
import type { Permission } from 'types/permission'
import { ZentakeContext } from 'utils/ZentakeContext'

type UsePermissions = {|
  isOwnerUser: boolean,
  isAdminUser: boolean,
  canArchiveResponse: boolean,
  canCreateClientProfile: boolean,
  canCreatePacketForm: boolean,
  canDeleteClientProfile: boolean,
  canDeletePacketForm: boolean,
  canDeleteResponse: boolean,
  canDisablePacketForm: boolean,
  canEditClientProfile: boolean,
  canEditPacketForm: boolean,
  canMergeClientProfile: boolean,
  canSendResponse: boolean,
  canViewClientList: boolean,
  canViewClientProfile: boolean,
  canViewResponse: boolean,
  canExportCsv: boolean,
  canExportPdf: boolean,
  canViewSubmissionReport: boolean,
  canViewGeneralSetting: boolean,
  canViewAutomation: boolean,
  canViewIntegrations: boolean,
  canManageTeamUsers: boolean,
  hasPermission: (permission: Permission) => boolean,
|}

function usePermissions(): UsePermissions {
  const { userDetails } = React.useContext(ZentakeContext)

  const hasPermission = React.useCallback(
    (permission: Permission) => {
      return (
        userDetails?.membership?.permissions_groups[0]?.permissions?.includes(
          permission
        ) || false
      )
    },
    [userDetails]
  )

  const isOwnerUser = React.useMemo(() => {
    return !!userDetails?.membership?.permissions_groups.some(
      (group) => group?.name === 'Owner'
    )
  }, [userDetails?.membership?.permissions_groups])

  const isAdminUser = React.useMemo(() => {
    return !!userDetails?.membership?.permissions_groups.some(
      (group) => group?.name === 'Administrator' || group?.name === 'Owner'
    )
  }, [userDetails?.membership?.permissions_groups])

  const canViewClientList = hasPermission(Permissions.CLIENT_PROFILE_LIST_VIEW)
  const canViewClientProfile = hasPermission(Permissions.CLIENT_PROFILE_VIEW)
  const canCreateClientProfile = hasPermission(
    Permissions.CLIENT_PROFILE_CREATE
  )
  const canEditClientProfile = hasPermission(Permissions.CLIENT_PROFILE_CREATE)
  const canMergeClientProfile = hasPermission(Permissions.CLIENT_PROFILE_MERGE)
  const canDeleteClientProfile = hasPermission(
    Permissions.CLIENT_PROFILE_DELETE
  )

  const canCreatePacketForm = hasPermission(
    Permissions.PACKET_FORM_BUILDING_CREATE
  )
  const canEditPacketForm = hasPermission(
    Permissions.PACKET_FORM_BUILDING_CREATE
  )
  const canDisablePacketForm = hasPermission(
    Permissions.PACKET_FORM_BUILDING_DISABLE
  )
  const canDeletePacketForm = hasPermission(
    Permissions.PACKET_FORM_BUILDING_DELETE
  )

  const canViewResponse = hasPermission(Permissions.PACKET_FORM_SUBMISSION_VIEW)
  const canSendResponse = hasPermission(Permissions.PACKET_FORM_SUBMISSION_SEND)
  const canArchiveResponse = hasPermission(
    Permissions.PACKET_FORM_SUBMISSION_ARCHIVE
  )
  const canDeleteResponse = hasPermission(
    Permissions.PACKET_FORM_SUBMISSION_DELETE
  )

  const canExportPdf = hasPermission(
    Permissions.PACKET_FORM_SUBMISSION_EXPORT_PDF
  )
  const canExportCsv = hasPermission(
    Permissions.PACKET_FORM_SUBMISSION_EXPORT_CSV
  )

  const canViewSubmissionReport = hasPermission(
    Permissions.REPORT_DATA_VIEW_SUBMISSION_REPORT
  )

  const canViewGeneralSetting = hasPermission(
    Permissions.SETTINGS_GENERAL_SETTING
  )
  const canViewAutomation = hasPermission(Permissions.SETTINGS_AUTOMATION)
  const canViewIntegrations = hasPermission(Permissions.SETTINGS_INTEGRATION)
  const canManageTeamUsers = hasPermission(Permissions.SETTINGS_TEAM_USER)

  return {
    isOwnerUser,
    isAdminUser,
    canArchiveResponse,
    canCreateClientProfile,
    canCreatePacketForm,
    canDeleteClientProfile,
    canDeletePacketForm,
    canDeleteResponse,
    canDisablePacketForm,
    canEditClientProfile,
    canEditPacketForm,
    canMergeClientProfile,
    canSendResponse,
    canViewClientList,
    canViewClientProfile,
    canViewResponse,
    canExportCsv,
    canExportPdf,
    canViewSubmissionReport,
    canViewGeneralSetting,
    canViewAutomation,
    canViewIntegrations,
    canManageTeamUsers,
    hasPermission,
  }
}

export default usePermissions
